import Icon from '@/components/ui/AppIcon';

interface SupportHeroProps {
  onContactClick: () => void;
  onEmergencyClick: () => void;
  onChatClick: () => void;
}

export default function SupportHero({ onContactClick, onEmergencyClick, onChatClick }: SupportHeroProps) {
  return (
    <section className="relative bg-gradient-to-br from-primary via-secondary to-primary py-24 lg:py-32 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, rgba(0, 212, 255, 0.15) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-accent/10 border border-accent/20 rounded-full">
              <Icon name="ShieldCheckIcon" size={20} className="text-accent" />
              <span className="font-cta text-sm text-accent tracking-wider">24/7 MISSION SUPPORT</span>
            </div>

            <h1 className="font-headline text-5xl lg:text-6xl font-bold text-foreground leading-tight">
              Expert Support for
              <span className="block text-accent mt-2">Mission-Critical Manufacturing</span>
            </h1>

            <p className="text-lg text-muted-foreground leading-relaxed max-w-xl">
              Our aerospace manufacturing specialists provide comprehensive technical support, project consultation, and strategic guidance to ensure your manufacturing missions succeed.
            </p>

            <div className="flex flex-wrap gap-4">
              <button
                onClick={onContactClick}
                className="group px-8 py-4 bg-mission-orange text-primary font-cta text-sm font-medium rounded-lg transition-all duration-300 hover:shadow-glow hover:-translate-y-1 flex items-center space-x-2"
              >
                <span>REQUEST CONSULTATION</span>
                <Icon name="ArrowRightIcon" size={20} className="group-hover:translate-x-1 transition-transform duration-300" />
              </button>

              <button
                onClick={onEmergencyClick}
                className="px-8 py-4 bg-card border border-red-500/40 text-foreground font-cta text-sm font-medium rounded-lg transition-all duration-300 hover:border-red-400 hover:shadow-lg flex items-center space-x-2"
              >
                <Icon name="PhoneIcon" size={20} className="text-red-400" />
                <span>EMERGENCY SUPPORT</span>
              </button>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-6 pt-8 border-t border-border">
              <div>
                <div className="font-headline text-3xl font-bold text-accent">{'<2h'}</div>
                <div className="text-sm text-technical-gray mt-1">Response Time</div>
              </div>
              <div>
                <div className="font-headline text-3xl font-bold text-accent">24/7</div>
                <div className="text-sm text-technical-gray mt-1">Availability</div>
              </div>
              <div>
                <div className="font-headline text-3xl font-bold text-accent">98%</div>
                <div className="text-sm text-technical-gray mt-1">Satisfaction</div>
              </div>
            </div>
          </div>

          {/* Right Content - Support Channels */}
          <div className="space-y-4">
            {[
              {
                icon: 'ChatBubbleLeftRightIcon',
                title: 'Live Technical Chat',
                description: 'Connect with AskWings AI Bot — trained on Wings Bharath capabilities',
                status: 'Available Now',
                statusColor: 'text-success',
                action: 'chat'
              },
              {
                icon: 'VideoCameraIcon',
                title: 'Video Consultation',
                description: 'Schedule detailed technical discussions with specialists',
                status: 'Book Session',
                statusColor: 'text-accent',
                action: 'scroll'
              },
              {
                icon: 'EnvelopeIcon',
                title: 'Technical Support Tickets',
                description: 'Submit detailed inquiries with file attachments',
                status: 'Create Ticket',
                statusColor: 'text-warning',
                action: 'scroll'
              },
              {
                icon: 'PhoneIcon',
                title: 'Emergency Hotline',
                description: 'Critical manufacturing issues requiring immediate attention',
                status: 'Call Now',
                statusColor: 'text-error',
                action: 'emergency'
              }
            ].map((channel, index) => {
              const handleClick = () => {
                if (channel.action === 'emergency') onEmergencyClick();
                else if (channel.action === 'chat') onChatClick();
                else onContactClick();
              };

              return (
                <div
                  key={index}
                  onClick={handleClick}
                  className="group p-6 bg-card border border-border rounded-lg hover:border-accent transition-all duration-300 hover:shadow-glow cursor-pointer"
                >
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center group-hover:bg-accent/20 transition-colors duration-300">
                      <Icon name={channel.icon as any} size={24} className="text-accent" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-headline text-lg font-semibold text-foreground group-hover:text-accent transition-colors duration-300">
                          {channel.title}
                        </h3>
                        <span className={`text-xs font-cta ${channel.statusColor}`}>
                          {channel.status}
                        </span>
                      </div>
                      <p className="text-sm text-technical-gray leading-relaxed">
                        {channel.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}